import React, { useCallback, useId } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

export interface ToggleSwitchProps {
  isVisible: boolean;
  onToggle: (visible: boolean) => void | Promise<void>;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  disabled?: boolean;
  className?: string;
  showLabel?: boolean;
  showIcon?: boolean;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

/** Eye (visible) icon */
const EyeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={clsx("w-4 h-4", className)} fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    <path
      fillRule="evenodd"
      d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
      clipRule="evenodd"
    />
  </svg>
);

/** Eye (hidden) icon */
const EyeOffIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={clsx("w-4 h-4", className)} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
      clipRule="evenodd"
    />
    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
  </svg>
);

/** Sizes config */
const sizeConfig = {
  sm: { containerWidth: 32, containerHeight: 16, thumbSize: 12 },
  md: { containerWidth: 44, containerHeight: 22, thumbSize: 18 },
  lg: { containerWidth: 56, containerHeight: 28, thumbSize: 24 },
};

const ToggleSwitch = React.memo<ToggleSwitchProps>(function ToggleSwitch({
  isVisible,
  onToggle,
  size = 'md',
  label,
  disabled = false,
  className = '',
  showLabel = true,
  showIcon = true,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) {
  const { containerWidth, containerHeight, thumbSize } = sizeConfig[size];
  const id = useId();

  const handleToggle = useCallback(() => {
    if (!disabled) {
      onToggle(!isVisible);
    }
  }, [disabled, isVisible, onToggle]);

  return (
    <div className={clsx("flex items-center gap-3", className)}>
      {/* Label */}
      {showLabel && label && (
        <label
          htmlFor={id}
          className={clsx(
            "text-sm font-medium text-gray-700 cursor-pointer select-none",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          {label}
        </label>
      )}

      {/* Icon */}
      {showIcon && (
        <div className="flex items-center justify-center">
          {isVisible ? <EyeIcon className="text-green-600" /> : <EyeOffIcon className="text-red-600" />}
        </div>
      )}

      {/* Switch */}
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={isVisible}
        aria-label={label || 'Toggle visibility'}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={handleToggle}
        className={clsx(
          "relative inline-flex items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none",
          isVisible ? "bg-red-500" : "bg-gray-300",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:shadow-md"
        )}
        style={{ width: containerWidth, height: containerHeight }}
      >
        <motion.span
          className="absolute bg-white rounded-full shadow-md"
          style={{
            width: thumbSize,
            height: thumbSize,
            top: (containerHeight - thumbSize) / 2,
          }}
          animate={{
            x: isVisible ? containerWidth - thumbSize - 2 : 2,
          }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30,
          }}
        />
      </button>
    </div>
  );
});

export { ToggleSwitch };
export default ToggleSwitch;
